import { useCallback, useEffect, useRef, useState } from "react";
import * as chatbotApi from "src/api/chatbot";
import { useChatStream } from "src/components/AI/hooks/useChatStream";
import type {
	ChatbotAttachableQuestion,
	ChatbotConversation,
	ChatbotConversationDetails,
	ChatbotMessageMetadata,
} from "src/components/GlobalChatbot/types";
import { getMessageText } from "src/helpers/aiMessage";
import { chatbotQueryKeys } from "src/hooks/chatbotQueryKeys";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";
import { PromptType } from "src/types/enums";

interface UseGlobalChatbotConversationParams {
	conversationId: string | null;
	onConversationCreated: (conversationId: string) => void;
}

export function useGlobalChatbotConversation({
	conversationId,
	onConversationCreated,
}: UseGlobalChatbotConversationParams) {
	// Wspólny ref dla wartości, które getBody musi widzieć w chwili wysyłki
	// streamu, a które nie mogą czekać na re-render (patrz komentarz niżej).
	const pendingBodyRef = useRef<{
		conversationId: string | null;
		questionId: number | null;
	}>({ conversationId, questionId: null });

	const loadedConversationIdRef = useRef<string | null>(null);
	const [isLoadingMessages, setIsLoadingMessages] = useState(false);
	const [details, setDetails] = useState<ChatbotConversationDetails | null>(
		null,
	);

	// W ciele renderu nadpisywałoby to pendingBodyRef.current.conversationId
	// wartością starego propa, jeśli re-render (np. wywołany przez pushMessage
	// w useChat) nadejdzie zanim conversationId ustawiony w sendMessage zdąży
	// się przebić przez commit stanu rodzica — stream leciałby z conversationId: 0.
	useEffect(() => {
		pendingBodyRef.current.conversationId = conversationId;
	}, [conversationId]);

	const {
		messages,
		sendMessage: sendChatMessage,
		setMessages,
		isStreaming,
		isSubmitted,
		isWaitingForFirstToken,
	} = useChatStream({
		promptType: PromptType.CONVERSATION,
		streamProtocol: "data",
		onFinish: () => {
			queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.limits });

			// Kontekst pytania (promptContext) przychodzi tylko z listy tur, nie ze
			// streamu. Refetch dopiero tutaj (zamknięcie połączenia) — przy text-end
			// odpowiedź może jeszcze nie być zapisana w bazie.
			const finishedConversationId = pendingBodyRef.current.conversationId;
			if (!finishedConversationId) return;
			chatbotApi
				.getConversationMessages(finishedConversationId)
				.then((conversation) => {
					if (
						pendingBodyRef.current.conversationId === finishedConversationId
					) {
						setMessages(conversation.messages);
						setDetails(conversation.details);
					}
				});
		},
		getBody: (chatMessages) => {
			const lastUserMessage = chatMessages
				.filter((m) => m.role === "user")
				.pop();
			return {
				specId: newApi.getUserSpecialization(),
				conversationId: Number(pendingBodyRef.current.conversationId),
				questionId: pendingBodyRef.current.questionId ?? undefined,
				userPrompt: lastUserMessage ? getMessageText(lastUserMessage) : "",
			};
		},
	});

	useEffect(() => {
		if (!conversationId) {
			loadedConversationIdRef.current = null;
			setMessages([]);
			setDetails(null);
			setIsLoadingMessages(false);
			return;
		}

		if (loadedConversationIdRef.current === conversationId) {
			return;
		}
		loadedConversationIdRef.current = conversationId;

		// Odpowiedź dla rozmowy, z której user już wyszedł, ignorujemy — inaczej
		// wolniejszy request nadpisałby wiadomości aktualnie otwartej rozmowy.
		const isStillActive = () =>
			loadedConversationIdRef.current === conversationId;

		setMessages([]);
		setDetails(null);
		setIsLoadingMessages(true);
		chatbotApi
			.getConversationMessages(conversationId)
			.then((conversation) => {
				if (!isStillActive()) return;
				setMessages(conversation.messages);
				setDetails(conversation.details);
			})
			.finally(() => {
				if (isStillActive()) setIsLoadingMessages(false);
			});
	}, [conversationId, setMessages]);

	const sendMessage = useCallback(
		async (text: string, question?: ChatbotAttachableQuestion | null) => {
			pendingBodyRef.current.questionId = question?.id ?? null;

			if (!pendingBodyRef.current.conversationId) {
				const conversation = await chatbotApi.createConversation(text);
				pendingBodyRef.current.conversationId = conversation.id;
				loadedConversationIdRef.current = conversation.id;
				queryClient.setQueryData<ChatbotConversation[]>(
					chatbotQueryKeys.conversations,
					(prev) => [conversation, ...(prev ?? [])],
				);
				onConversationCreated(conversation.id);
			}
			const metadata: ChatbotMessageMetadata | undefined = question
				? { questionPreview: question.preview }
				: undefined;
			await sendChatMessage({ text, metadata });
		},
		[onConversationCreated, sendChatMessage],
	);

	return {
		messages,
		details,
		sendMessage,
		isStreaming,
		isSubmitted,
		isWaitingForFirstToken,
		isLoadingMessages,
	};
}
